/**
 * Normalize agent markdown before react-markdown + remark-gfm.
 *
 * Ontology Builder (and some relay paths) can deliver payloads where:
 * - newlines arrived as literal ``\n`` / ``\r\n`` escape sequences
 * - GFM table rows are glued to adjacent prose on the same line
 * - headings / lists / tables lack the blank lines CommonMark needs
 *
 * Mirrors gaxl-ontology-builder-agent ``response_message`` polish so the
 * ESA panel renders tables and lists the same as a well-formed draft.
 */

const HEADING_RE = /^(#{1,6}\s+\S.*|\*\*[^*].*?\*\*:?\s*)$/;
const LIST_RE = /^(\s*[-*+] |\s*\d+\.\s+)\S/;
const TABLE_RE = /^\|/;
/** Compact or padded GFM row with at least two cells. */
const TABLE_ROW_RE = /\|[^|\n]+\|[^|\n]*\|/;

function lineKind(line: string): 'blank' | 'table' | 'list' | 'heading' | 'text' {
  const stripped = line.trim();
  if (!stripped) return 'blank';
  if (TABLE_RE.test(stripped)) return 'table';
  if (LIST_RE.test(line) || LIST_RE.test(stripped)) return 'list';
  if (HEADING_RE.test(stripped)) return 'heading';
  return 'text';
}

/** Turn literal ``\n`` / ``\t`` escapes into real control characters when present. */
export function unescapeMarkdownEscapes(text: string): string {
  let out = text.replace(/\r\n/g, '\n');
  if (!out.includes('\\n') && !out.includes('\\r') && !out.includes('\\t')) {
    return out;
  }
  return out
    .replace(/\\r\\n/g, '\n')
    .replace(/\\n/g, '\n')
    .replace(/\\r/g, '\n')
    .replace(/\\t/g, '\t');
}

/**
 * Peel GFM table rows that were concatenated onto surrounding prose
 * (e.g. ``174 items |Area|Item|Count|`` or ``|Total|174| - **Examples**``).
 */
export function separateGluedMarkdownTables(text: string): string {
  if (!text) return text;

  const out: string[] = [];
  for (const line of text.split('\n')) {
    const tableStart = line.search(TABLE_ROW_RE);
    if (tableStart > 0) {
      const before = line.slice(0, tableStart).trimEnd();
      if (before) out.push(before);
      pushTableSegment(out, line.slice(tableStart).trim());
      continue;
    }

    const trail = line.match(/^(\|(?:[^|\n]*\|)+)\s+([^|].*)$/);
    if (trail) {
      out.push(trail[1]);
      out.push(trail[2].trim());
      continue;
    }

    out.push(line);
  }
  return out.join('\n');
}

function pushTableSegment(out: string[], segment: string): void {
  const trail = segment.match(/^(\|(?:[^|\n]*\|)+)\s+([^|].*)$/);
  if (trail) {
    out.push(trail[1]);
    out.push(trail[2].trim());
    return;
  }
  out.push(segment);
}

/** Insert CommonMark blank lines so headings, tables, and lists render. */
export function ensureMarkdownBlockSpacing(text: string): string {
  if (!(text || '').trim()) return text;

  const lines = text.split('\n');
  const out: string[] = [];
  for (const line of lines) {
    const kind = lineKind(line);
    const prev = out.length > 0 ? out[out.length - 1] : undefined;
    const prevKind = prev !== undefined ? lineKind(prev) : 'blank';

    const needsBlankBefore =
      (kind === 'table' && prevKind !== 'blank' && prevKind !== 'table') ||
      (kind === 'list' && (prevKind === 'heading' || prevKind === 'text' || prevKind === 'table')) ||
      ((kind === 'heading' || kind === 'text') && (prevKind === 'table' || prevKind === 'list')) ||
      (kind === 'heading' && (prevKind === 'heading' || prevKind === 'text'));

    if (needsBlankBefore) {
      out.push('');
    }
    out.push(line.replace(/\s+$/, ''));
  }
  return out.join('\n').trim();
}

function collapseRunawayBlankLines(text: string): string {
  const lines = text.split('\n').map(l => l.replace(/\s+$/, ''));
  const out: string[] = [];
  let blankRun = 0;
  for (const line of lines) {
    if (!line.trim()) {
      blankRun += 1;
      if (blankRun <= 1) out.push('');
      continue;
    }
    blankRun = 0;
    out.push(line);
  }
  return out.join('\n').trim();
}

/**
 * Split common Ontology Builder / DESO glue patterns where a bold heading or
 * section label shares a line with the following list / CTA.
 *
 * Examples:
 * - ``**Summary** - **Build mode**: …``
 * - ``**Rules** (showing 10 of 57): - Documents …``
 * - ``…non-negative Reply **preview** …``
 */
export function repairGluedMarkdownStructure(text: string): string {
  if (!text) return text;

  let out = text;

  // Bold section heading immediately followed by a list marker (dash or digit)
  // on the *same line* only. Use [ \t]+ — not \s+ — so newline + nested-list
  // indent (``  - Type: …`` under ``- **Label** (`ukey`)``) is preserved.
  // ``**Summary** - **Build mode**`` / ``**Rules**: - item`` / ``**Rules** (n): - item``
  out = out.replace(
    /(\*\*[^*\n]+?\*\*(?:[ \t]*\([^)\n]*\))?:?)[ \t]+(?=-\s|\d+\.\s)/g,
    '$1\n\n',
  );

  // Closing CTA after a list item: ``…non-negative Reply **preview**``
  out = out.replace(
    /([^\n])\s+(Reply\s+\*\*(?:preview|approve|publish|reject)\*\*)/gi,
    '$1\n\n$2',
  );

  // Soft-break consecutive bold section labels jammed mid-prose after a table/list.
  // ``…look. **Classes** (showing 10 of 24): … **Attributes** (showing``
  // Also ``…`a-books`. **Summary**``
  out = out.replace(
    /([.!?`])\s+(\*\*(?:Summary|Classes|Attributes|Rules|Examples|Properties)\*\*)/g,
    '$1\n\n$2',
  );
  out = out.replace(
    /(\S)\s+(\*\*(?:Attributes|Rules|Examples|Properties)\*\*\s*\()/g,
    '$1\n\n$2',
  );

  return out;
}

/**
 * Full normalize pipeline for agent message text before Markdown render.
 */
export function normalizeAgentMarkdown(text: string): string {
  if (!(text || '').trim()) return text;
  let out = unescapeMarkdownEscapes(text);
  out = separateGluedMarkdownTables(out);
  out = repairGluedMarkdownStructure(out);
  out = ensureMarkdownBlockSpacing(out);
  out = collapseRunawayBlankLines(out);
  return out;
}
